import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch as useFetchV4 } from '@/modules/core/composables/useFetch'
import { useFetch } from '@/modules/shared/composables/use-fetch'
import { addItem, addItems, clearItems } from '@/modules/shared/utils/store'
import { CID } from '@/modules/shared/utils/store.types'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import type { EntityExpanded, EntityInsights } from './better-entity-store'

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

type OtherEntity = {
  _cid: CID
  _key: string
  id: string
  admins: {
    _cid: CID
    id: string
    name: string
  }[]
  filing_folder_root_id: string
  name: string
  date: string
  type: string
  tax_id: string
  input_capital: boolean
  management_fee_percentage: number
  number_of_investors: number
  carried_interest: number
  street1: string
  street2: string
  city: string
  zip: string
  country: string
  disbursement_method: string
  disbursement_wire_bank_name: string
  disbursement_wire_bank_address: string
  disbursement_wire_bank_routing_number: string
  disbursement_wire_bank_account_number: string
  disbursement_wire_bank_notes: string
  disbursement_wire_bank_swift_code: string
  disbursement_wire_account_name: string
  disbursement_wire_for_further_credit_to: string
  disbursement_check_receiver_name: string
  disbursement_other_details: string
  screening_check_id: string
  screening_check_status: string
  screening_accreditation_id: string
  screening_accreditation_status: string
  entities: string[]
  profile_status: string
}

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

type OtherEntityMap = Map<CID, OtherEntity>

export const useOtherEntityStore = defineStore('investing/otherEntityStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/${route.params.slug}/investing`)
  const notificationStore = useNotificationStore()

  const items = ref<OtherEntityMap>(new Map())
  const other_entities = computed(() => Array.from(items.value.keys()).map((key) => items.value.get(key)))

  // Expanded entities owned by the currently-viewed FundingEntity. Mirrors how
  // `useIndividualStore` keeps `individual.value.entities` for the redesigned
  // SPV/Funds list pages: each row carries merged insights/structure so the
  // shared composables (column visibility, lifecycle filter, search) can read
  // `item.insights.*` without a second hop.
  const expandedEntities = ref<EntityExpanded[]>([])
  const listEntities = computed(() => expandedEntities.value)

  const addOtherEntity = async (otherEntity: OtherEntity) => {
    let url = `${baseUrl.value}/other-entity/add`

    const { data, error } = await useFetch(url).post({ otherEntity }).json<{ data: OtherEntity }>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }

    notificationStore.enqueue('success', 'Associated entity was successfully added')
    addItem(items, data.value.data)
    return data.value.data
  }

  const fetchOtherEntities = async (investable_type?, investable_id?) => {
    clearItems(items)
    let url = `${baseUrl.value}/other-entities`

    if (investable_type && investable_id) url = `${baseUrl.value}/${investable_type}/${investable_id}/other-entities`
    const { data, error } = await useFetch(url).get().json<{ data: OtherEntity[] }>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }

    addItems(items, data.value.data)
  }

  const fetchOtherEntity = async (id: string) => {
    let url = `${baseUrl.value}/other-entity/${id}`

    const { data, error } = await useFetch(url).get().json<{ data: OtherEntity }>()

    if (error.value) {
      notificationStore.enqueue('error', 'Associated entity failed to load')
      return
    }

    addItem(items, data.value.data)
  }

  // Mirrors useIndividualStore.fetchEntities: hydrate every entity behind the
  // FundingEntity with merged /investor-sets/:id + /insights, scoped via
  // shareholder_cids so insights respect the LP's actual holdings under this
  // funding entity (not whole-entity totals).
  //
  // Concurrency is capped at MAX_PARALLEL_FETCHES — a FundingEntity can hold
  // 10+ entities, and each row costs two requests (show + insights). Without
  // a cap, a bare Promise.all queues every request and the browser's per-host
  // connection limit (~6) turns a 12-entity load into a 1.5-minute waterfall.
  const MAX_PARALLEL_FETCHES = 6
  const fetchEntities = async (
    entityIds: string[],
    { slug, shareholder_cids }: { slug: string; shareholder_cids?: string[] },
  ) => {
    const params = new URLSearchParams()
    if (!!shareholder_cids) {
      params.set('shareholder_cids', shareholder_cids.join(','))
    }

    const queryParams = params.toString() ? `?${params}` : ''

    const fetchOne = async (entityId: string) => {
      const [entity, insights] = await Promise.all([
        useFetchV4(`/v4/${slug}/investing/investor-sets/${entityId}${queryParams}`).get<EntityExpanded>(),
        useFetchV4(`/v4/${slug}/investing/investor-sets/${entityId}/insights${queryParams}`).get<EntityInsights>(),
      ])
      return {
        ...entity,
        insights: insights.insights,
        structure: insights.structure,
        document_status: insights.document_status,
      }
    }

    // Worker-pool: spawn N workers that pull from a shared cursor. As each
    // worker finishes a fetch, it claims the next index. Keeps in-flight
    // requests at MAX_PARALLEL_FETCHES regardless of total entity count.
    const results: EntityExpanded[] = new Array(entityIds.length)
    let cursor = 0
    const worker = async () => {
      while (true) {
        const i = cursor++
        if (i >= entityIds.length) break
        results[i] = await fetchOne(entityIds[i])
      }
    }

    try {
      const workerCount = Math.min(MAX_PARALLEL_FETCHES, entityIds.length)
      await Promise.all(Array.from({ length: workerCount }, () => worker()))
      expandedEntities.value = results
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const resetEntities = () => {
    expandedEntities.value = []
  }

  const removeOtherEntity = async (other_entity_id) => {
    const { data, error } = await useFetch(`${baseUrl.value}/other-entity/${other_entity_id}/remove`)
      .delete()
      .json<{}>()

    if (error.value) {
      console.log(error.value)
      notificationStore.enqueue('error', 'Associated entity failed to remove')
      return
    }
    notificationStore.enqueue('success', 'Associated entity successfully removed')
  }

  const updateOtherEntity = async (payload) => {
    const { data, error } = await useFetch(`${baseUrl.value}/other-entity/${payload.funding_entity.id}/update`)
      .post({ otherEntity: payload })
      .json<{}>()

    if (error.value) {
      console.log(error.value)
      notificationStore.enqueue('error', 'Associated entity failed to update')
      return
    }
    notificationStore.enqueue('success', 'Associated entity successfully updated')
  }

  return {
    items,
    other_entities,
    listEntities,

    addOtherEntity,
    fetchOtherEntities,
    fetchOtherEntity,
    fetchEntities,
    resetEntities,
    removeOtherEntity,
    updateOtherEntity,
  }
})
